import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginRequest, useLoginMutation } from 'app/apis/authApi';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { SerializedError } from '@reduxjs/toolkit';

interface LoginErrorState {
  isUsernameEmpty: boolean | null;
  isPasswordEmpty: boolean | null;
  haveChangedInputValuesSinceLastError: boolean;
}

const defaultErrorMessage =
  'Something went wrong. Please try again later. If the problem persists, please contact Ruler Analytics support.';

const Login: React.FC = () => {
  const [formState, setFormState] = useState<LoginRequest>({
    password: '',
    username: ''
  });

  const [errorState, setErrorState] = useState<LoginErrorState>({
    isUsernameEmpty: null,
    isPasswordEmpty: null,
    haveChangedInputValuesSinceLastError: false
  });

  const [login, { isError, error }] = useLoginMutation();
  const navigate = useNavigate();

  const handleLoginError = (
    error: FetchBaseQueryError | SerializedError | undefined
  ) => {
    if (error) {
      let errorMessage: string = defaultErrorMessage;
      if ('status' in error) {
        // you can access all properties of `FetchBaseQueryError` here
        errorMessage =
          'originalStatus' in error && error.originalStatus === 401
            ? 'Incorrect email address and / or password.'
            : defaultErrorMessage;

        return (
          <div>
            <div>{errorMessage}</div>
          </div>
        );
      } else {
        return <div>{errorMessage}</div>;
      }
    }
  };
  const handleChange = ({
    target: { name, value }
  }: React.ChangeEvent<HTMLInputElement>) => {
    setErrorState({
      isUsernameEmpty: false,
      isPasswordEmpty: false,
      haveChangedInputValuesSinceLastError: true
    });
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <section className="h-screen w-full flex bg-neutral-100">
        <div className="p-6 lg:p-0 w-full lg:w-1/2 flex flex-col sm:items-center justify-center bg-white">
          <img
            src="/media/logos/ruler.svg"
            className="h-14"
            alt="Ruler Analytics"
          />
          <form
            onSubmit={(e) => {
              const handler = async () => {
                e.preventDefault();

                if (!formState.username && !formState.password) {
                  setErrorState({
                    isUsernameEmpty: true,
                    isPasswordEmpty: true,
                    haveChangedInputValuesSinceLastError: false
                  });
                  return;
                }

                if (!formState.username) {
                  setErrorState({
                    isUsernameEmpty: true,
                    isPasswordEmpty: false,
                    haveChangedInputValuesSinceLastError: false
                  });
                  return;
                }

                if (!formState.password) {
                  setErrorState({
                    isUsernameEmpty: false,
                    isPasswordEmpty: true,
                    haveChangedInputValuesSinceLastError: false
                  });
                  return;
                }

                const formData: FormData = new FormData();
                formData.append('username', formState.username);
                formData.append('password', formState.password);

                setErrorState({
                  isUsernameEmpty: false,
                  isPasswordEmpty: false,
                  haveChangedInputValuesSinceLastError: false
                });

                const result: any = await login(formData);

                if (!result.error) {
                  navigate('/dashboard');
                }
              };
              handler().catch((error) => console.log(error));
            }}
            className="sm:w-108 mt-6 p-5 border border-neutral-200 shadow-md bg-white"
          >
            {isError && !errorState.haveChangedInputValuesSinceLastError && (
              <div className="overflow-hidden text-center border p-1 my-2 border-error-300 rounded-md bg-error-100">
                <div className=" text-xs text-error-300 italic">
                  {handleLoginError(error)}
                </div>
              </div>
            )}
            <div>
              <label htmlFor="username" className="block mb-1 text-sm">
                Username
              </label>
              {errorState?.isUsernameEmpty && (
                <p className="text-error-300 text-xs italic">
                  Please enter your username.
                </p>
              )}
              <input
                type="username"
                name="username"
                id="username"
                className="w-full border border-neutral-200 rounded py-2 px-4 outline-none focus:border-primary-700 focus:bg-white placeholder-neutral-400"
                placeholder="Enter your username"
                onChange={handleChange}
                autoComplete="on"
              />
            </div>
            <div className="mt-4">
              <label htmlFor="password" className="block mb-1 text-sm">
                Password
              </label>
              {errorState?.isPasswordEmpty && (
                <p className="text-error-300 text-xs italic">
                  Please enter your password.
                </p>
              )}
              <input
                type="password"
                name="password"
                id="password"
                className="w-full border border-neutral-200 rounded py-2 px-4 outline-none focus:border-primary-700 focus:bg-white placeholder-neutral-400"
                placeholder="Enter your password"
                onChange={handleChange}
                autoComplete="on"
              />
            </div>
            <button
              className="text-center rounded transition-all duration-75 text-white border border-primary-600 bg-primary-600 hover:border-primary-500 hover:bg-primary-500 active:border-primary-600 active:bg-primary-600  px-4 py-3 w-full mt-5"
              type="submit"
            >
              Login
            </button>
          </form>

          <p className="mt-6 text-xs text-center">
            Don&apos;t have an account yet{'?'}&nbsp;
            <a
              className="text-xs text-blue"
              href="https://www.ruleranalytics.com/book-demo/"
            >
              Get Started
            </a>
          </p>
          <p className="mt-6 text-xs text-center">
            View our&nbsp;
            <a className="text-xs text-blue" href="/privacy-policy">
              Privacy Policy
            </a>
          </p>
        </div>
        <div className="hidden lg:block w-1/2 bg-secondary-50 h-full">
          <a
            className="hidden lg:block  bg-no-repeat bg-right bg-cover"
            href="https://www.ruleranalytics.com/marketing-mix-modelling"
          >
            <img src="media/welcome-page/welcome-content.png" />
          </a>
        </div>
      </section>
    </>
  );
};

export default Login;
